/* global espn espn_ui jQuery */
// ES6 promise polyfill for IEs
require('es6-promise').polyfill()
/**
 * Shim startsWith when not available
 * From : https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/String/startsWith
 *
 * @param {String}    searchString  string which needs to be searched for
 * @param {Number}    position      position from which searchString should be searched
 * @returns {Boolean}               true if string starts with given string
 */
if (!String.prototype.startsWith) {
  String.prototype.startsWith = function (searchString, position) {
    position = position || 0
    return this.substr(position, searchString.length) === searchString
  }
}

import React from 'react'
import { render, unmountComponentAtNode } from 'react-dom'
import { Router, match, useRouterHistory } from 'react-router'
import { createHistory, useBeforeUnload } from 'history'
import { Provider } from 'react-redux'
import { routesDefault, routesCricinfo } from 'routes'
import selectn from 'selectn'
import moment from 'moment-timezone'
import { fetchComponentData, mapPageToTemplate } from './lib'
import configureStore from './stores/configureStore'
import config from '../config'
import shell from '../app/lib/utils/shell'
import { validatePageConfig, setPageConfig, onRenderComplete } from '../app/actions'
import { docReady } from '../app/lib/utils/docReady'
import isReactPath      from '../app/lib/utils/isReactPath'
import getRequestParams from '../app/lib/utils/getRequestParams'
import { translatePath, translateQueryKey } from '../app/lib/utils/translateURI'
import setMomentLocale from '../app/lib/utils/momentLocales'
const history = useRouterHistory(useBeforeUnload(createHistory))()
const store = configureStore(window.__INITIAL_STATE__, history)
const { dispatch } = store
let edition = selectn( 'page.edition', store.getState() ) || {}
let routes = !edition.isEspnCricinfo ? routesDefault : routesCricinfo
let initialLoad = true

// Set window._reactInMem=true on init
window._reactInMem = true

function updateApp(location, pathnameChange, prevTemplate) {
  if(typeof location !== 'undefined') {

    //Translation - without this client side routing won't work
    let translatedPath = translatePath(edition, location.pathname, true)
    let translatedQuery = translateQueryKey(edition, location.query, true)
    location.pathname = translatedPath
    location.query = translatedQuery
    match({ routes, location }, (err, redirectLocation, renderProps = {}) => {

      const currState = store.getState()
      const appProps = selectn( 'page.appProps', currState ) || {}
      edition = selectn( 'page.edition', currState ) || {}
      const language = edition.language || 'en'
      moment.locale(language, setMomentLocale(language))
      /* modify params to correct sport/league and pageType */
      const routeParams = getRequestParams( edition, renderProps, location ) || {}
      renderProps.params = routeParams
      const { pageType, sport, platform, team, league } = routeParams
      const template = mapPageToTemplate({ pageType, sport, platform })

      try{
        shell.transition.preRender(edition, sport, template, team, league)
      }catch(e) {
        console.log(e)
      }

      // end transition immediately, if template is unchanged or has platform param (webview)
      if(prevTemplate === template || platform) {
        shell.transition.end()
      }
      // call fetchComponentData only if initialState is undefined,
      // or when app is not server rendered
      if(typeof window.__INITIAL_STATE__ === 'undefined') {
        const prefetchActions = initialLoad ? [] : [ validatePageConfig ]
        if(espn.siteType) {
          appProps.siteType = espn.siteType
        }
        fetchComponentData(dispatch, renderProps, renderProps.location || location, prefetchActions, edition, appProps)
          .then(() => {
            renderApp(false, pathnameChange)
            shell.transition.end()
          })
      }else {
        delete window.__INITIAL_STATE__
        renderApp(false, pathnameChange)
      }

    })
  }
}

docReady(function connectShell() {

  // Intercept the `history.change` event from the shell
  jQuery(document).on('history.change', (e, state) => {
    // exit if transition is between non react pages
    if(!selectn('Helpers.page.react',espn_ui) && !state.react) {
      return
    }

    //When navigating from FITT use normal navigation
    if(state && (state.fromFITT)) {
      window.location = state.url
    }

    shell.transition.start()
    // if prev page is jQuery, current is React,
    if(!selectn('Helpers.page.react', espn_ui) && state.react) {
      // Follows the same logic from original `history.change`
      if(state.meta && state.meta.type === 'gamepackage' && state.url) {
        window.location = state.url
        return
      }
      try  {
        espn_ui.Helpers.page.load(state, null, { eventType: 'history' })
      } catch(e) {
        console.error(e)
      }
      // notify history.change
      dispatch(() => { return { type: 'HISTORY_CHANGE' } })
      // Unmount the whole app, to circumvent mismatched id errors
      unmountComponentAtNode(document.getElementById(config.uid))
      // Re-render the app
      renderApp(true)
      // Set page.react to true
      try {
        espn_ui.Helpers.page.react = true
      } catch(e) {
        console.error(e)
      }
    }
    // End page transition (show/hide)
    window.setTimeout(shell.transition.end, 250)
  })

  // Subscribe to `analytics.loaded` and pull page analytics data
  //For data lite we are using image tracking (analytics.liteTrack) for analytics so need to have separate condition to load ads config
  jQuery.subscribe('analytics.loaded', function (data) {
    // Pull ads and analytics from the page shell and store it in the app state
    dispatch(setPageConfig({ ads: espn.ads && espn.ads.config, analytics: data }))
  })

  // In webviews, we have to set the cmsEnbldSpnsrdCntntPrvdrs flag here because
  // the above event for analytics being loaded won't fire, yet we still need this
  // for sponsored content to be displayed
  let state = store.getState()
  if(selectn('page.params.platform', state)) {
    dispatch(setPageConfig({ ads: espn.ads && espn.ads.config }))
  }
  if(selectn('page.appProps.siteType', state) === 'data-lite') {
    dispatch(setPageConfig({ ads: espn.ads && espn.ads.config }))
  }


  // Listen to `espn.defer.ready`, and trigger app load on init
  var startedInteractivity = false
  var startInteractivity = function () {
    if(startedInteractivity) {
      return
    }
    startedInteractivity = true
    // Before navigation handler
    // if `history.change` takes user to a page
    // that matches react-router routes, but isn't handled by react,
    // prevent location change in react-router.
    history.listenBefore((location) => {
      //console.info('location', location)
      return isReactPath( window.location.hostname, location.pathname, edition ) || false
    })

    // Add history listener
    history.listen((location) => {
      espn_ui.Helpers.page = espn_ui.Helpers.page || {}
      // transition.start(), hides the app container.
      // We prevent it from hiding it when location change is
      // triggered for query filters, ex: scoreboards
      // TODO: improve url change check
      const pathnameChange = location.pathname !== espn_ui.Helpers.page.pathname
      if(!initialLoad && pathnameChange) {
        window.scrollTo(0,0)
        shell.transition.start()
      }
      // If initialLoad or if page id is react
      if(initialLoad || selectn('Helpers.page.react', espn_ui)) {
        updateApp(location, pathnameChange, espn_ui.Helpers.page.template)
      }
    })

    // Set window._reactInMem=false before unload
    history.listenBeforeUnload(function () {
      console.log('listenBeforeUnload')
      window._reactInMem = false
    })

    // TEMP: Click handler for playing videos on mobile devices,
    // TODO: Remove after CSS and clickable areas for play icons are fixed
    jQuery('body').on('click', '.highlight.module_bloom_behavior:not(.video-play-button-external,.app_partial)', function (event) {
      if (espn_ui.device.isMobile) {
        espn_ui.Helpers.video.playFromElement(jQuery('.video-play-button', this), false, event)
      }
    })

    if(espn.siteType === 'data-lite' && typeof espn.ads.loadGPT === 'function') {
      espn.ads.loadGPT()
    }
  }

  if(espn.siteType === 'data-lite' && espn_ui.docAndDeferReady === true) {
    startInteractivity()
  } else {
    jQuery.subscribe('espn.defer.ready', startInteractivity)
  }
})
/*
This method is exposed at window level so that mobile app can trigger reduxion from static landing page
It accepts absolute gamepackage url as a param which will init reduxion with gameid in game url
*/
window.load = function (gameUrl) {
  let linkObj = document.createElement('a')
  linkObj.href = gameUrl
  let url = linkObj.pathname
  let query = linkObj.search
  if(typeof query != 'undefined') {
    url += query
  }
  history.push(url)
}
window.reset = function () {
  let platform = 'ios'
  if(/\/android\//.test(location.href)) {
    platform = 'android'
  }
  let query = window.location.search
  let resetUrl = '/cricket/static/game/'+ platform +'/webview/'
  if(typeof query !== 'undefined') {
    resetUrl += query
  }
  history.push(resetUrl)
}
function renderApp(historyChange, pathnameChange) {
  render(
    <Provider store={store}>
      <Router uid={config.uid} children={routes} history={history} />
    </Provider>,
    document.getElementById(config.uid),
    () => {
      dispatch(onRenderComplete({ initialLoad, historyChange, pathnameChange }))
      initialLoad = false
    }
  )
}



// WEBPACK FOOTER //
// ./client.js